import type { components } from '@pba/types-openapi-client';
import { type ChangeRequest, type ChangeRequestStatus, createChangeRequest } from './change-request';
import type { Entity } from './entity';
import { type Image, createImage } from './image';
import { type OrganizationList, createOrganizationList } from './organization';
import type { CommunityDetailDataObject } from './services';
import { type UserList, createUserList } from './user';

export type CommunityDetail = {
  administrators: UserList[];
  changeRequests: ChangeRequest[];
  default: boolean;
} & CommunityList;

export type CommunityList = {
  autoJoin: boolean;
  coverPhoto?: Image;
  description: string;
  featuredInChannels: boolean;
  htmlDescription: string;
  membersCount: number;
  meta?: CommunityMeta;
  organization: OrganizationList;
  privacy: CommunityPrivacy;
  profilePhoto?: Image;
  settings: CommunitySettings;
  tags: Tag[];
} & CommunitySlim;

export type CommunityMeta = {
  patientTypes?: MetaItem[];
  serviceTypes?: MetaItem[];
  telehealthVisitDurations?: number[];
};

export type CommunityPrivacy = components['schemas']['CommunityPrivacyEnum'];

export type CommunitySettings = {
  allowComments?: boolean;
  allowedEventsAdmin?: ProgramEventType[];
  allowedEventsMember?: ProgramEventType[];
  allowInvites?: boolean;
  allowPosts?: boolean;
  allowUnsubscribe?: boolean;
  viewMembers?: boolean;
};

export type CommunitySlim = {
  organizationId: string;
  privacy: CommunityPrivacy;
  settings: CommunitySettings;
} & Entity;

export type MetaItem = {
  children?: MetaItem[];
  getTranslation: (language?: string) => string;
  name: string;
  translations: MetaItemTranslations;
};

export type MetaItemTranslations = Record<string, string>;

export type ProgramEventType = components['schemas']['EventTypeEnum'];

export type Tag = components['schemas']['Tag'];

type CommunityListGlobalDataObject = components['schemas']['CommunityListGlobal'];

type CommunityMetaDataObject = CommunityListGlobalDataObject['meta'];

type CommunityMetaItemDataObject = NonNullable<CommunityMetaDataObject['patient_types']>[number];

const createCommunitySettings = (data: components['schemas']['SettingsBase']): CommunitySettings => ({
  allowComments: data.allow_comments,
  allowedEventsAdmin: data.allowed_events_admin?.filter((eventType) => eventType !== 'telehealth_visit'),
  allowedEventsMember: data.allowed_events_member?.filter((eventType) => eventType !== 'telehealth_visit'),
  allowInvites: data.allow_invites,
  allowPosts: data.allow_posts,
  allowUnsubscribe: data.allow_unsubscribe,
  viewMembers: data.view_members,
});

const createMetaItem = (data: CommunityMetaItemDataObject): MetaItem => ({
  children: data.children?.map((metaItem) => createMetaItem(metaItem)),
  getTranslation(language?: string) {
    if (language === 'en') {
      return data.translations.en;
    }

    if (language === 'es') {
      return data.translations.es;
    }

    return data.translations.en;
  },
  name: data.name,
  translations: data.translations,
});

const createCommunityMeta = (data: CommunityMetaDataObject): CommunityMeta => ({
  patientTypes: data.patient_types?.map((metaItem) => createMetaItem(metaItem)),
  serviceTypes: data.service_types?.map((metaItem) => createMetaItem(metaItem)),
});

const createCommunitySlim = (data: components['schemas']['CommunitySlim']): CommunitySlim => ({
  id: data.id,
  name: data.name,
  organizationId: data.organization_id,
  privacy: data.privacy,
  settings: createCommunitySettings(data.settings),
});

const createCommunityList = (data: CommunityListGlobalDataObject): CommunityList => ({
  ...createCommunitySlim(data),
  autoJoin: data.auto_join,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks cover_photo as always present, but API responses may omit it
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  description: data.description,
  featuredInChannels: data.featured_in_channels,
  htmlDescription: data.html_description,
  membersCount: data.members_count,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks meta as always present, but API responses may omit it
  meta: data.meta ? createCommunityMeta(data.meta) : undefined,
  organization: createOrganizationList(data.organization),
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks profile_photo as always present, but API responses may omit it
  profilePhoto: data.profile_photo ? createImage(data.profile_photo) : undefined,
  tags: data.tags,
});

export const createCommunityDetail = (data: CommunityDetailDataObject): CommunityDetail => ({
  ...createCommunityList(data),
  administrators: data.administrators.map((administrator) => createUserList(administrator)),
  changeRequests: data.change_requests.map((request) => createChangeRequest(request)),
  default: data.default,
});

export const hasChangeRequestStatus = ({
  community,
  status,
}: {
  community: CommunityDetail;
  status: ChangeRequestStatus;
}): boolean =>
  community.changeRequests.some((request) => request.kind === 'community_request_to_join' && request.status === status);
